<template>
  <div class="new-create-appointment">
    <PageTitle
      v-if="!embedMode"
      :title="displayText.title"
      hideBtn
      icon="chevron_left"
      @iconClick="$router.go(-1)"
    />
    <BaseElForm
      v-if="loaded"
      ref="rootFormRef"
      :model="context"
      :rules="formRules"
      label-position="top"
      class="form-wrapper"
    >
      <AppointmentBaseInfoBlock @modeChange="onModeChange" />

      <!-- Keeper 車輛資訊 -->
      <AppointmentVehicleInfoBlock v-if="showControl.vehicleInfo" />
      <!-- 餐廳選擇 -->
      <AppointmentRestaurantBlock v-if="showControl.foodpandaRestaurant" />
      <transition name="el-fade-in-linear">
        <template v-if="!refreshing.mode">
          <!-- 一般模式 -->
          <AppointmentNormalMode v-if="showControl.normalMode" />
          <!-- 多時段模式 -->
          <AppointmentBatchOrderMode v-if="showControl.batchOrderMode" />
          <!-- 多服務模式 -->
          <AppointmentOtherServicesMode v-if="showControl.otherReservationMode" />
        </template>
      </transition>
      <!-- 同意書簽名 -->
      <transition name="el-fade-in-linear">
        <AppointmentSignedNameBlock v-if="showControl.signedName" />
      </transition>
      <transition name="el-fade-in-linear">
        <AppointmentPaymentBlock v-if="showControl.paymentInfo" />
      </transition>
      <AppointmentOtherInfoBlock />
    </BaseElForm>

    <div v-if="embedMode" style="margin-top: 30px">
      <FlexLayout justify="end">
        <BaseElButton style="width: 100px" class="btn" plain @click="onCancel">
          {{ $t('common.button.cancel.text') }}
        </BaseElButton>
        <BaseElButton
          v-loading="loading.submit"
          style="width: 100px"
          :disabled="disabledControl.btn.submit"
          class="btn"
          type="primary"
          @click="onCreateAppointment"
        >
          {{ $t('common.button.confirm.text') }}
        </BaseElButton>
      </FlexLayout>
    </div>

    <PageFixedFooter
      v-if="!embedMode"
      :confirmLoading="loading.submit"
      :disabledConfirm="disabledControl.btn.submit"
      @confirm="onCreateAppointment"
      @cancel="onCancel"
    />
  </div>
</template>

<script>
import { appointmentModeType, featureKeys, useCreateAppointment } from '@/use/useCreateAppointment'
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
} from 'vue'
import AppointmentBaseInfoBlock from './components/AppointmentBaseInfoBlock.vue'
import AppointmentOtherInfoBlock from './components/AppointmentOtherInfoBlock.vue'
import AppointmentBatchOrderMode from './components/AppointmentBatchOrderMode.vue'
import AppointmentPaymentBlock from './components/AppointmentPaymentBlock.vue'
import AppointmentSignedNameBlock from './components/AppointmentSignedNameBlock.vue'
import AppointmentOtherServicesMode from './components/AppointmentOtherServicesMode.vue'
import PageFixedFooter from '@/components/Footer/PageFixedFooter.vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { get, find } from 'lodash'
import { CreateAppointmentOrderV2, CreateBatchAppointmentOrderV2 } from '@/api/reservation'
import { maxNumberRules, noEmptyRules } from '@/validation'
import AppointmentNormalMode from './components/AppointmentNormalMode.vue'
import { useInvoiceCarrier } from '@/use/useInvoiceCarrier'

import formUtils from '@/utils/form'
import PageTitle from '@/components/Title/PageTitle.vue'
import { FindMember } from '@/api/member'
import { apiFormatAdaptor } from '@/utils/api'
import { useShop } from '@/use/shop'
import { useQueryClient } from '@tanstack/vue-query'
import { clearAvailabilityQueries } from '@/use/useAvailabilityQuery'
import { i18n } from '@/plugins/i18n/i18n'
import AppointmentVehicleInfoBlock from './components/AppointmentVehicleInfoBlock.vue'
import { checkAction } from '@/utils/permissions'
import AppointmentRestaurantBlock from './components/AppointmentRestaurantBlock.vue'

export default defineComponent({
  name: 'NewCreateAppointment',
  components: {
    PageTitle,
    AppointmentBaseInfoBlock,
    AppointmentVehicleInfoBlock,
    AppointmentBatchOrderMode,
    AppointmentPaymentBlock,
    AppointmentSignedNameBlock,
    AppointmentOtherInfoBlock,
    AppointmentOtherServicesMode,
    AppointmentNormalMode,
    PageFixedFooter,
    AppointmentRestaurantBlock,
  },
  props: {
    embedMode: {
      type: Boolean,
      default: false,
    },
    preset: {
      type: Object,
    },
  },
  setup (props, { emit }) {
    const {
      memberData,
      context,
      init,
      configData,
      resourceData,
      cart,
      addAppointmentCartItem,
      resetCart,
      resetContext,
      allCartItemReady,
      formatBatchOrderPayload,
      formatOrderPayload,
      composeAllCartItemReservations,
      getMemberResourceData,
      paymentInitialized,
      presetData,
    } = useCreateAppointment()
    const { getCarrierNumRules } = useInvoiceCarrier('appointmentCheckout')
    const queryClient = useQueryClient()
    const rootFormRef = ref(null)
    const loaded = ref(false)
    const router = useRouter()
    const route = useRoute()
    const { shopId } = useShop()
    const displayText = computed(() => {
      return {
        title: i18n.t('newCreateAppointment.title'),
      }
    })
    const showControl = computed(() => {
      const controls = {
        normalMode: false,
        vehicleInfo: false,
        otherReservationMode: false,
        batchOrderMode: false,
        paymentInfo: false,
        // 若為 batchOrder 模式，簽名同意書不顯示
        signedName: checkAction(featureKeys.useSignConsentFrom) && allCartItemReady.value,
        foodpandaRestaurant: false,
      }

      const toggleMode = () => {
        if (context.appointmentMode === appointmentModeType.normal) {
          controls.normalMode = true
        } else if (context.appointmentMode === appointmentModeType.otherReservation) {
          controls.otherReservationMode = true
        } else if (context.appointmentMode === appointmentModeType.batchOrder) {
          controls.batchOrderMode = true
        }
      }

      if (context.userType === 'member') {
        if (context.member) {
          toggleMode()
          controls.vehicleInfo = true
          controls.foodpandaRestaurant = true
        }
      } else if (context.userType === 'guest') {
        toggleMode()
        controls.vehicleInfo = true
        controls.foodpandaRestaurant = false
      }

      // keeper 是否啟用
      if (
        !find(get(configData.reservation, 'customRules'), { rule: 'keeperVehicleCreationCheck' })
      ) {
        controls.vehicleInfo = false
      }

      // foodpanda 餐廳選擇是否啟用
      if (!find(get(configData.reservation, 'customRules'), { rule: 'foodpandaRestaurantCreationCheck' })) {
        controls.foodpandaRestaurant = false
      }

      // 第一次 ready 後付款子樹保持掛載，編輯時段造成的暫時 false 不再卸載
      if (allCartItemReady.value || paymentInitialized.value) {
        controls.paymentInfo = true
      }

      // 如果目前模式是 batchOrder，禁用簽名顯示
      if (context.appointmentMode === appointmentModeType.batchOrder) {
        controls.signedName = false
      }

      return controls
    })

    const loading = reactive({
      submit: false,
    })

    const disabledControl = computed(() => {
      const controls = {
        btn: {
          submit: false,
        },
      }

      if (context.isProcessing) {
        controls.btn.submit = true
      }

      return controls
    })

    const refreshing = reactive({
      mode: false,
    })

    // 基本表單規則
    const formRules = computed(() => {
      const rules = {
        member: [noEmptyRules()],
        'payment.method': [noEmptyRules()],
      }

      if (context.userType === 'guest') {
        delete rules.member
        rules['guest.name'] = [noEmptyRules()]
        rules['guest.phone'] = [noEmptyRules()]
      }

      if (get(configData.reservation, 'userCommentSetting.userCommentRequired', false)) {
        rules.comment = [noEmptyRules('請填寫備註欄位')]
      }

      // 根據不同預約模式生成不同的規則
      if (context.appointmentMode === appointmentModeType.normal) {
        rules.service = [noEmptyRules()]
        rules.serviceUnit = [noEmptyRules()]
      } else if (context.appointmentMode === appointmentModeType.batchOrder) {
        rules.service = [noEmptyRules()]
        rules.serviceUnit = [noEmptyRules()]
      } else if (context.appointmentMode === appointmentModeType.otherReservation) {
        rules.peopleCount = [maxNumberRules(1)]

        const cartItemCount = cart.value.length
        for (let count = 0; count < cartItemCount; count++) {
          rules[`[${count}].service`] = [noEmptyRules()]
          rules[`[${count}].serviceUnit`] = [noEmptyRules()]
        }
      }

      const chargeType = get(context.checkoutDetail, 'chargeType')

      if (get(configData.checkoutInvoice, 'enable') && chargeType === 'checkout') {
        rules['invoice.email'] = [noEmptyRules()]
        if (context.invoice.type === 'B') {
          rules['invoice.companyTitle'] = [noEmptyRules()]
          rules['invoice.taxId'] = [noEmptyRules()]
        } else if (context.invoice.type === 'carrier') {
          rules['invoice.carrierType'] = [noEmptyRules()]
          rules['invoice.carrierNum'] = getCarrierNumRules(context.invoice.carrierType)
        }
      }

      if (get(configData.checkout, 'enableCashbackDeduction') && chargeType === 'checkout') {
        const cashbackBalance = get(memberData, 'cashback.balance')
        const cashbackLimit = get(context, 'discount.cashback.limit', 0)
        let maxValue
        if (cashbackBalance < cashbackLimit) {
          maxValue = cashbackBalance
        } else {
          maxValue = cashbackLimit
        }

        rules['discount.cashback.use'] = [maxNumberRules(maxValue)]
      }

      return rules
    })

    // 檢查所有購物車項目表單
    const checkCartItemsForm = async () => {
      let allPass = true
      for (const cartItem of cart.value) {
        const formRef = get(cartItem, 'formRef')
        if (formRef) {
          const formPass = await formUtils.checkForm(formRef)
          if (!formPass) allPass = false
        }
      }
      return allPass
    }

    // 檢查多時段設定表單
    const checkPeriodSettingForm = async () => {
      let allPass = true
      for (const formRef of context.periodSettingFormRefs) {
        const formPass = await formUtils.checkForm(formRef)
        if (!formPass) allPass = false
      }
      return allPass
    }

    const checkCommonInfoForm = async () => {
      let pass = true
      const otherUserInfoformRef = get(context.otherUserInfo, 'formRef')
      if (otherUserInfoformRef) pass = await formUtils.checkForm(otherUserInfoformRef)
      return pass
    }

    // 建立預約訂單
    const onCreateAppointment = async () => {
      if (loading.submit) return
      loading.submit = true
      const appointmentMode = context.appointmentMode

      const cartItemsFormPass = await checkCartItemsForm()
      const commonInfoFormPass = await checkCommonInfoForm()
      const rooFormPass = await formUtils.checkForm(rootFormRef.value)

      if (!cartItemsFormPass || !commonInfoFormPass || !rooFormPass) {
        window.$message.warning('尚有欄位未填寫，或填寫資訊有誤，請檢查 !')
        loading.submit = false
        return
      }
      if (appointmentMode === appointmentModeType.batchOrder) {
        if (!(await checkPeriodSettingForm())) {
          window.$message.warning('多時段設定有誤或未填寫，請檢查 !')
          loading.submit = false
          return
        }
      }
      let apiMethod = CreateAppointmentOrderV2
      let payload = formatOrderPayload.value
      if (appointmentMode === appointmentModeType.batchOrder) {
        apiMethod = CreateBatchAppointmentOrderV2
        payload = formatBatchOrderPayload.value
      }

      const [, err] = await apiMethod(payload)
      loading.submit = false
      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success('建立預約成功 !')

      clearAvailabilityQueries(queryClient)
      resetCart()
      resetContext()
      if (props.embedMode) {
        emit('created')
        emit('close')
      } else {
        router.go(-1)
      }
    }

    const onReset = async () => {
      resetCart()
      resetContext()
      await nextTick()
      if (props.preset) {
        presetData.value = { ...props.preset }
      }
    }

    const onCancel = () => {
      resetCart()
      resetContext()
      if (props.embedMode) {
        emit('close')
      } else {
        router.go(-1)
      }
    }

    const checkPresetMember = async () => {
      const userType = get(presetData.value, 'userType')
      if ((userType && userType === 'member') || route.query.member) {
        const [res, err] = await apiFormatAdaptor(FindMember)({
          shopId: shopId.value,
          id: get(presetData.value, 'memberId') || route.query.member,
        })
        if (err) {
          window.$message.error(err)
          return
        }
        context.member = res
        context.userType = 'member'

        getMemberResourceData('member')
        getMemberResourceData('classTickets')
        getMemberResourceData('cashback')
      } else if (userType === 'guest') {
        context.userType = 'guest'
        context.guest.name = get(presetData.value, 'userName')
        context.guest.phone = get(presetData.value, 'userPhone')
      }
    }

    const onModeChange = async () => {
      // 因為切換模式會重置購物車，導致 cartItem.formRef 變成 null
      // 因此要手動重新開關 coreFlowBlock 來觸發 onMounted 生命週期來更新 cartItem.formRef
      refreshing.mode = true
      await nextTick()
      setTimeout(() => {
        refreshing.mode = false
      }, 0)
    }

    onBeforeMount(async () => {
      if (props.preset) {
        presetData.value = { ...props.preset }
      }

      await init()
      await checkPresetMember()
      loaded.value = true
    })

    onBeforeUnmount(() => {
      presetData.value = null
      resetCart()
      resetContext()
    })

    return {
      loading,
      rootFormRef,
      formRules,
      context,
      loaded,
      configData,
      resourceData,
      cart,
      addAppointmentCartItem,
      showControl,
      onReset,
      onCreateAppointment,
      onCancel,
      formatBatchOrderPayload,
      formatOrderPayload,
      composeAllCartItemReservations,
      onModeChange,
      refreshing,
      displayText,
      disabledControl,
      presetData,
    }
  },
})
</script>

<style lang="postcss" scoped>
.form-wrapper {
  @apply flex flex-col gap-[24px];
}
</style>
